<script>
  import { get } from 'svelte/store';
  import { onDestroy, onMount } from 'svelte';
  import { APP_FONT_SCALE_STEPS, APP_THEMES, DEFAULT_APP_SETTINGS, MAX_TOKENS_LIMIT, getDisplayNameFallback, getFontScaleFactor } from '../lib/app-settings.js';
  import { appSettingsStore, classifyChatError, defaultModelStore, loadModels, modelsStore, pushRestoreStore, saveAppSettings, showFlash, userStore } from '../lib/stores.js';
  import { compressImageDataUrl } from '../lib/image-compress.js';
  import { api } from '../lib/api.js';
  import { TEXT } from '../lib/ui-text.js';
  import { groupModels, modelLabel } from '../lib/model-picker.js';
  import DevicesCard from '../lib/components/DevicesCard.svelte';
  import BuildStamp from '../lib/components/BuildStamp.svelte';
  import {
    getCurrentSubscription,
    isIos,
    isPushSupported,
    isStandalone,
    subscribeToPush,
    unsubscribeFromPush,
  } from '../lib/push.js';

  let error = null;

  let form = { ...DEFAULT_APP_SETTINGS };
  // Этап 16: приём приглашения ведёт сюда с setup-passkey=1 — предложить Face ID сразу.
  const promptPasskeySetup = typeof window !== 'undefined'
    && new URLSearchParams(window.location.hash.split('?')[1] || '').get('setup-passkey') === '1';
  let fileInput;

  // Web Push (7.5): состояние этого устройства.
  let pushSupported = isPushSupported();
  let pushServerEnabled = false;
  let pushPublicKey = null;
  let pushSubscribed = false;
  let pushBusy = false;
  let pushMessage = '';
  $: pushNeedsInstall = pushSupported === false && isIos() && !isStandalone();
  $: pushStatusText = !pushSupported
    ? (pushNeedsInstall ? TEXT.push.needInstall : TEXT.push.unsupported)
    : (!pushServerEnabled
        ? TEXT.push.serverDisabled
        : (pushSubscribed ? TEXT.push.enabled : TEXT.push.disabled));

  async function loadPushState() {
    try {
      const config = await api.getPushConfig();
      pushServerEnabled = Boolean(config?.enabled);
      pushPublicKey = config?.publicKey || null;
    } catch (e) {
      pushServerEnabled = false;
    }
    if (pushSupported) {
      try {
        pushSubscribed = Boolean(await getCurrentSubscription());
      } catch (e) {
        pushSubscribed = false;
      }
    }
  }

  async function enablePush() {
    pushBusy = true;
    pushMessage = '';
    try {
      const result = await subscribeToPush(pushPublicKey);
      if (result === 'granted') {
        pushSubscribed = true;
        pushRestoreStore.set(false);
      } else if (result === 'denied') {
        pushMessage = TEXT.push.denied;
      } else {
        pushMessage = TEXT.push.unsupported;
      }
    } catch (e) {
      console.error('push subscribe failed', e);
      pushMessage = TEXT.push.failed;
    } finally {
      pushBusy = false;
    }
  }

  async function disablePush() {
    pushBusy = true;
    pushMessage = '';
    try {
      await unsubscribeFromPush();
      pushSubscribed = false;
      pushRestoreStore.set(false);
    } catch (e) {
      console.error('push unsubscribe failed', e);
      pushMessage = TEXT.push.failed;
    } finally {
      pushBusy = false;
    }
  }

  async function testPush() {
    pushBusy = true;
    pushMessage = '';
    try {
      await api.sendTestPush();
      pushMessage = TEXT.push.testSent;
    } catch (e) {
      pushMessage = classifyChatError(e, TEXT.push.failed);
    } finally {
      pushBusy = false;
    }
  }

  $: previewName = getDisplayNameFallback(form, get(userStore));
  // Размер применяется к документу только после сохранения, поэтому образец
  // в форме показывает выбранную ступень заранее.
  $: fontScaleFactor = getFontScaleFactor(form.fontScale);
  $: fontScalePercent = Math.round(fontScaleFactor * 100);
  $: availableModels = $modelsStore;
  $: modelGroups = groupModels(availableModels);

  function syncFromStore() {
    form = { ...$appSettingsStore };
  }

  async function readFileAsDataUrl(file) {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error || new Error('Не удалось прочитать файл.'));
      reader.readAsDataURL(file);
    });
  }

  async function handleAvatarChange(event) {
    const file = event.currentTarget.files?.[0];
    if (!file) {
      return;
    }
    if (!file.type.startsWith('image/')) {
      error = 'Можно загрузить только изображение.';
      return;
    }
    try {
      error = null;
      const raw = await readFileAsDataUrl(file);
      // Аватар сжимаем до 256px — иначе сырой файл (сотни КБ) тащится в каждом
      // /api/settings/app при старте и resume.
      await autosave({ avatarDataUrl: await compressImageDataUrl(raw, { maxDimension: 256, quality: 0.85 }) });
    } catch (e) {
      error = e.message || 'Не удалось загрузить изображение.';
    }
  }

  function clearAvatar() {
    if (fileInput) {
      fileInput.value = '';
    }
    autosave({ avatarDataUrl: null });
  }

  async function handleNotificationsToggle() {
    if (!form.browserNotificationsEnabled) {
      autosave();
      return;
    }
    if (typeof window === 'undefined' || !('Notification' in window)) {
      form = { ...form, browserNotificationsEnabled: false };
      error = 'Браузерные уведомления не поддерживаются.';
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      form = { ...form, browserNotificationsEnabled: false };
      error = permission === 'denied'
        ? 'Доступ к уведомлениям запрещён в браузере.'
        : 'Разрешение на уведомления не выдано.';
      return;
    }
    autosave();
  }

  function normalizeModelNumber(value, fallback, min, max) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < min || numeric > max) {
      return fallback;
    }
    return numeric;
  }

  function normalizeModelSettings() {
    const selectedModel = typeof form.defaultModel === 'string' ? form.defaultModel.trim() : '';
    const modelIsAllowed = !availableModels.length || availableModels.some((model) => model.id === selectedModel);
    form = {
      ...form,
      defaultModel: modelIsAllowed ? selectedModel : get(defaultModelStore),
      temperature: normalizeModelNumber(form.temperature, DEFAULT_APP_SETTINGS.temperature, 0, 2),
      maxTokens: Math.trunc(normalizeModelNumber(form.maxTokens, DEFAULT_APP_SETTINGS.maxTokens, 1, MAX_TOKENS_LIMIT)),
    };
  }

  // ── Автосохранение (В6, 1.27.6) ──
  // Кнопки «Сохранить» внизу длинной формы больше нет: «←» молча выбрасывал правки,
  // а тема и размер текста не применялись, пока до неё не долистаешь. Каждое
  // изменение уходит сразу, сохранения идут по очереди, последнее побеждает.
  let saveState = 'idle'; // idle | saving | saved | error
  let savedTimer = null;
  let saveChain = Promise.resolve();

  function autosave(patch = {}) {
    form = { ...form, ...patch };
    const snapshot = form;
    saveState = 'saving';
    error = null;
    saveChain = saveChain.then(async () => {
      try {
        await saveAppSettings(snapshot);
        saveState = 'saved';
        clearTimeout(savedTimer);
        savedTimer = setTimeout(() => {
          if (saveState === 'saved') {
            saveState = 'idle';
          }
        }, 2000);
      } catch (e) {
        saveState = 'error';
        error = classifyChatError(e, 'Не удалось сохранить общие настройки.');
      }
    });
    return saveChain;
  }

  function saveModelSettings() {
    normalizeModelSettings();
    autosave();
  }

  function resetModelSettings() {
    autosave({ temperature: DEFAULT_APP_SETTINGS.temperature, maxTokens: DEFAULT_APP_SETTINGS.maxTokens });
  }

  onDestroy(() => clearTimeout(savedTimer));

  function goBack() {
    window.location.hash = '/chats';
  }

  syncFromStore();

  onMount(() => {
    loadPushState();
    // Всегда обновляем список моделей (whitelist мог измениться на бэке); при ошибке
    // сети остаётся ранее загруженный modelsStore.
    loadModels().catch((e) => {
      if (!get(modelsStore).length) {
        error = classifyChatError(e, 'Не удалось загрузить список моделей.');
      }
    });
  });
</script>

<div class="app-settings-page">
  <header class="header">
    <button class="back-btn" on:click={goBack} aria-label="Назад">←</button>
    <h1>Общие настройки</h1>
    <span class="save-state" class:error={saveState === 'error'} aria-live="polite">
      {#if saveState === 'saving'}{TEXT.settings.saving}{:else if saveState === 'saved'}{TEXT.settings.saved}{/if}
    </span>
  </header>

  <div class="settings-content">
    <p class="hint autosave-hint">{TEXT.settings.autosaveHint}</p>
    {#if error}
      <div class="error-message" role="alert">{error}</div>
    {/if}
    <section class="card profile-card">
      <h2>Мой профиль</h2>
      <div class="profile-preview">
        {#if form.avatarDataUrl}
          <img class="avatar-image" src={form.avatarDataUrl} alt="Аватар пользователя" />
        {:else}
          <div class="avatar-fallback">{previewName.slice(0, 1).toUpperCase()}</div>
        {/if}
        <div class="profile-meta">
          <strong>{previewName}</strong>
          <span>Идентификатор: {$userStore}</span>
        </div>
      </div>

      <label class="form-group">
        <span>Имя</span>
        <input bind:value={form.displayName} on:change={() => autosave()} type="text" maxlength="80" placeholder="Как вас показывать в приложении" />
      </label>

      <div class="form-group">
        <span>Аватарка</span>
        <div class="avatar-actions">
          <label class="file-btn">
            <input
              bind:this={fileInput}
              type="file"
              accept="image/*"
              aria-label="Выбрать файл аватарки"
              on:change={handleAvatarChange}
            />
            <span>{form.avatarDataUrl ? 'Заменить' : 'Выбрать файл'}</span>
          </label>
          {#if form.avatarDataUrl}
            <button type="button" class="btn-secondary" on:click={clearAvatar}>Очистить</button>
          {/if}
        </div>
      </div>
    </section>

    <section class="card">
      <h2>Оформление</h2>
      <label class="form-group">
        <span>Тема</span>
        <select bind:value={form.theme} on:change={() => autosave()}>
          {#each APP_THEMES as theme}
            <option value={theme}>
              {theme === 'auto' ? 'Как в системе' : theme === 'dark' ? 'Тёмная' : theme === 'sepia' ? 'Сепия' : 'Светлая'}
            </option>
          {/each}
        </select>
      </label>

      <div class="form-group">
        <span id="font-scale-label">Размер текста</span>
        <div class="scale-picker" role="radiogroup" aria-labelledby="font-scale-label">
          {#each APP_FONT_SCALE_STEPS as step}
            <button
              type="button"
              role="radio"
              aria-checked={form.fontScale === step.value}
              aria-label={`${Math.round(step.factor * 100)} процентов`}
              class="scale-option"
              class:active={form.fontScale === step.value}
              style={`font-size: calc(var(--scale-base) * ${step.factor})`}
              on:click={() => autosave({ fontScale: step.value })}
            >Аа</button>
          {/each}
        </div>
        <p class="scale-percent">{fontScalePercent}%</p>
        <div class="scale-preview" style={`font-size: calc(var(--scale-base) * ${fontScaleFactor})`}>
          <p class="preview-author">Петрович</p>
          <p class="preview-text">Так будет выглядеть текст сообщений при выбранном размере.</p>
        </div>
      </div>
    </section>

    <section class="card">
      <h2>Уведомления и звуки</h2>
      <label class="toggle">
        <input type="checkbox" bind:checked={form.soundEnabled} on:change={() => autosave()} />
        <span>Включить звуки в приложении</span>
      </label>
      <label class="toggle">
        <input type="checkbox" bind:checked={form.sendSoundEnabled} disabled={!form.soundEnabled} on:change={() => autosave()} />
        <span>Звук отправки сообщения</span>
      </label>
      <label class="toggle">
        <input type="checkbox" bind:checked={form.responseSoundEnabled} disabled={!form.soundEnabled} on:change={() => autosave()} />
        <span>Звук ответа ассистента</span>
      </label>
      <label class="toggle">
        <input type="checkbox" bind:checked={form.browserNotificationsEnabled} on:change={handleNotificationsToggle} />
        <span>Браузерные уведомления при новом ответе</span>
      </label>
      <p class="hint">Уведомления работают, пока приложение открыто во вкладке или PWA.</p>
    </section>

    <section class="card">
      <h2>{TEXT.push.title}</h2>
      <p class="push-status" data-testid="push-status">{pushStatusText}</p>
      {#if pushSupported && pushServerEnabled}
        <div class="push-actions">
          {#if pushSubscribed}
            <button type="button" class="btn-secondary" disabled={pushBusy} on:click={disablePush}>{TEXT.push.disable}</button>
            <button type="button" class="btn-secondary" disabled={pushBusy} on:click={testPush}>{TEXT.push.sendTest}</button>
          {:else}
            <button type="button" class="btn-primary" disabled={pushBusy} on:click={enablePush}>{TEXT.push.enable}</button>
          {/if}
        </div>
      {/if}
      {#if pushMessage}
        <p class="hint push-message">{pushMessage}</p>
      {/if}
      <p class="hint">{TEXT.push.hint}</p>
    </section>

    <DevicesCard {promptPasskeySetup} />

    <!-- Библиотека шаблонов чата (этап 24). -->
    <section class="card">
      <h2>{TEXT.templates.settingsLink}</h2>
      <p class="hint">{TEXT.templates.settingsHint}</p>
      <a class="btn-secondary templates-link" href="#/templates">{TEXT.templates.title}</a>
    </section>

    <!-- В14 (1.27.6): параметры модели действуют на все чаты и семье обычно не нужны —
         свёрнуты в «Для опытных», с понятными подписями и возвратом к рекомендуемым. -->
    <details class="card advanced">
      <summary>{TEXT.settings.advancedTitle}</summary>
      <p class="hint">{TEXT.settings.advancedHint}</p>
      <label class="form-group">
        <span>{TEXT.settings.defaultModelLabel}</span>
        <select bind:value={form.defaultModel} on:change={saveModelSettings}>
          {#each modelGroups as group (group.provider)}
            <optgroup label={group.title}>
              {#each group.items as model (model.id)}
                <option value={model.id}>{modelLabel(model.id)}</option>
              {/each}
            </optgroup>
          {/each}
        </select>
        <span class="hint">{TEXT.settings.defaultModelHint}</span>
      </label>

      <label class="form-group">
        <span>{TEXT.settings.temperatureLabel}</span>
        <input bind:value={form.temperature} on:change={saveModelSettings} type="number" min="0" max="2" step="0.1" inputmode="decimal" />
        <span class="hint">{TEXT.settings.temperatureHint}</span>
      </label>

      <label class="form-group">
        <span>{TEXT.settings.maxTokensLabel}</span>
        <input bind:value={form.maxTokens} on:change={saveModelSettings} type="number" min="1" max={MAX_TOKENS_LIMIT} step="1" inputmode="numeric" />
        <span class="hint">{TEXT.settings.maxTokensHint}</span>
      </label>

      <button type="button" class="btn-secondary" on:click={resetModelSettings}>{TEXT.settings.resetDefaults}</button>
    </details>

    <BuildStamp />
  </div>
</div>

<style>
  .save-state {
    margin-left: auto;
    font-size: 0.8125rem;
    color: var(--text-secondary);
  }

  .save-state.error {
    color: var(--error-text);
  }

  .autosave-hint {
    margin: 0;
  }

  .advanced summary {
    min-height: 44px;
    display: flex;
    align-items: center;
    font-weight: 600;
    cursor: pointer;
  }

  .advanced[open] summary {
    margin-bottom: var(--spacing-sm);
  }

  .app-settings-page {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }

  .header {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
    padding: var(--spacing-md) var(--spacing-lg);
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-color);
  }

  .back-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    /* В11 (1.27.2): цель касания 44px (HIG); отрицательный margin сохраняет плотность шапки. */
    width: 44px;
    height: 44px;
    margin: -6px;
    border: none;
    border-radius: 8px;
    background: none;
    color: var(--text-primary);
    font-size: 1.25rem;
  }

  .settings-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg);
    padding: var(--spacing-lg);
    max-width: 720px;
    width: 100%;
    margin: 0 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .card {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
    min-width: 0;
    padding: var(--spacing-lg);
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: 16px;
    box-shadow: var(--shadow-sm);
  }

  .card h2 {
    font-size: 1.1rem;
  }

  .profile-preview {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
    min-width: 0;
  }

  .avatar-image,
  .avatar-fallback {
    width: 72px;
    height: 72px;
    border-radius: 24px;
    object-fit: cover;
  }

  .avatar-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--accent-color);
    color: #fff;
    font-size: 1.75rem;
    font-weight: 700;
  }

  .profile-meta {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
    min-width: 0;
    color: var(--text-secondary);
    overflow-wrap: anywhere;
  }

  .push-status {
    margin: 0 0 0.75rem;
    color: var(--text-secondary);
  }

  .push-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-bottom: 0.5rem;
  }

  .push-message {
    color: var(--text-primary);
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
    min-width: 0;
  }

  .form-group input,
  .form-group select {
    max-width: 100%;
  }

  /* rem уже умножен на выбранный масштаб, поэтому делением возвращаем базовый
     размер браузера — образец показывает ступени в абсолютных размерах, а не
     относительно текущей настройки. */
  .scale-picker,
  .scale-preview {
    --scale-base: calc(1rem / var(--font-scale, 1));
  }

  .scale-picker {
    display: flex;
    align-items: stretch;
    gap: var(--spacing-xs);
  }

  .scale-option {
    flex: 1;
    min-width: 0;
    min-height: 44px;
    padding: var(--spacing-xs);
    border: 1px solid var(--border-color);
    border-radius: 10px;
    background: var(--bg-tertiary);
    color: var(--text-secondary);
    line-height: 1.2;
    cursor: pointer;
  }

  .scale-option.active {
    border-color: var(--accent-color);
    background: var(--accent-color);
    color: #fff;
  }

  .scale-percent {
    margin: 0;
    color: var(--text-muted);
    font-size: 0.8125rem;
    text-align: center;
  }

  .scale-preview {
    padding: var(--spacing-sm) var(--spacing-md);
    border-radius: 12px;
    background: var(--assistant-bg);
    color: var(--assistant-text);
  }

  .preview-author {
    margin: 0 0 0.25em;
    font-size: 0.8125em;
    font-weight: 600;
    color: var(--text-secondary);
  }

  .preview-text {
    margin: 0;
    line-height: 1.45;
  }

  .avatar-actions {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    flex-wrap: wrap;
    min-width: 0;
  }

  /* Нативный <input type="file"> задаёт ширину своим внутренним содержимым
     («Выбрать файл» + имя файла) и не сжимается — на узком экране он распирал
     карточку за правую границу. Прячем его внутрь кнопки-лейбла (тот же приём,
     что у кнопки вложения в композере) — ширину теперь задаёт наш текст. */
  .file-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 0 1rem;
    border: 1px solid var(--border-color);
    border-radius: 10px;
    background: var(--bg-tertiary);
    color: var(--text-primary);
    font-size: 0.9375rem;
    cursor: pointer;
    overflow: hidden;
  }

  .file-btn input {
    position: absolute;
    inset: 0;
    opacity: 0;
    padding: 0;
    cursor: pointer;
  }

  .toggle {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
  }

  .hint {
    color: var(--text-secondary);
    font-size: 0.9rem;
  }

  .error-message {
    padding: var(--spacing-md);
    border-radius: 12px;
    background: var(--error-bg);
    color: var(--error-text);
  }


  .btn-primary,
  .btn-secondary {
    min-height: 44px;
    padding: 0.75rem 1rem;
    border-radius: 12px;
    border: 1px solid var(--border-color);
  }

  .btn-primary {
    background: var(--accent-color);
    color: #fff;
    border-color: transparent;
  }

  .btn-secondary {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
</style>
